import React, { useState, useEffect } from 'react';
import { FaCode, FaUsers, FaDatabase, FaTools, FaLayerGroup } from 'react-icons/fa';
import { motion } from 'framer-motion';
import Container from '../common/Container';
import useSkills from '../../hooks/useSkills';
import '../../styles/Skills.css';

const Skills = () => {
    const {
        theme,
        t,
        skillsRef,
        categoryIconNames,
        categoryColors
    } = useSkills();

    const [activeCategory, setActiveCategory] = useState(null);
    const [isInView, setIsInView] = useState(false);

    useEffect(() => {
        const currentRef = skillsRef.current;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsInView(true);
                }
            },
            { threshold: 0.1 }
        );
        
        if (currentRef) {
            observer.observe(currentRef);
        }
        
        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [skillsRef]);

    // Función para mapear nombres de iconos a componentes de iconos
    const getCategoryIcon = (iconName) => {
        switch(iconName) {
            case 'code': return <FaCode />;
            case 'layer-group': return <FaLayerGroup />;
            case 'database': return <FaDatabase />;
            case 'tools': return <FaTools />;
            default: return <FaCode />;
        }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                duration: 0.5,
                ease: "easeOut"
            }
        }
    };

    const handleCategoryHover = (index) => {
        setActiveCategory(index);
    };

    const handleCategoryLeave = () => {
        setActiveCategory(null);
    };

    return (
        <section id="skills" className={`skills ${theme}`} ref={skillsRef}>
            <Container>
                <motion.h2 
                    className="section-title"
                    initial={{ opacity: 0, y: -20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6 }}
                >
                    {t.title}
                    <span className="title-accent"></span>
                </motion.h2>
                
                <div className="skills-container">
                    {/* Habilidades Técnicas */}
                    <motion.div 
                        className="skills-category technical-skills"
                        initial="hidden"
                        animate={isInView ? "visible" : "hidden"}
                        variants={containerVariants}
                    >
                        <motion.div 
                            className="main-category-header"
                            variants={itemVariants}
                        >
                            <div className="icon-pulse">
                                <FaCode className="main-category-icon" />
                            </div>
                            <h3>{t.technical.title}</h3>
                        </motion.div>

                        <div className="skill-categories-row">
                            {t.technical.categories.map((category, index) => (
                                <motion.div 
                                    key={index} 
                                    className={`skill-category-card ${activeCategory === index ? 'active' : ''}`}
                                    variants={itemVariants}
                                    onMouseEnter={() => handleCategoryHover(index)}
                                    onMouseLeave={handleCategoryLeave}
                                    whileHover={{ 
                                        scale: 1.03,
                                        boxShadow: "0 15px 30px rgba(0, 0, 0, 0.15)"
                                    }}
                                >
                                    <div 
                                        className="category-card-header" 
                                        style={{
                                            backgroundColor: `${categoryColors[category.title]}15`, 
                                            borderColor: categoryColors[category.title]
                                        }}
                                    >
                                        <span 
                                            className="category-icon" 
                                            style={{color: categoryColors[category.title]}}
                                        >
                                            {getCategoryIcon(categoryIconNames[category.title])}
                                        </span>
                                        <h4 className="category-title">{category.title}</h4>
                                    </div>
                                    <ul className="skill-list">
                                        {category.skills.map((skill, skillIndex) => (
                                            <motion.li 
                                                key={skillIndex} 
                                                className="skill-item"
                                                initial={{ opacity: 1, x: 0 }}
                                                animate={{ opacity: 1, x: 0 }}
                                            >
                                                <span className="skill-arrow">→</span>
                                                <span className="skill-text">{skill}</span>
                                                <span 
                                                    className="skill-progress"
                                                    style={{
                                                        backgroundColor: categoryColors[category.title],
                                                        width: activeCategory === index ? '100%' : '0%'
                                                    }}
                                                ></span>
                                            </motion.li>
                                        ))}
                                    </ul>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>

                    {/* Habilidades Blandas */}
                    <motion.div 
                        className="skills-category soft-skills"
                        initial="hidden"
                        animate={isInView ? "visible" : "hidden"}
                        variants={containerVariants}
                    >
                        <motion.div 
                            className="main-category-header"
                            variants={itemVariants}
                        >
                            <div className="icon-pulse">
                                <FaUsers className="main-category-icon" />
                            </div>
                            <h3>{t.soft.title}</h3>
                        </motion.div>

                        <div className="soft-skills-grid">
                            {t.soft.skills.map((skill, index) => (
                                <motion.div 
                                    key={index} 
                                    className="soft-skill-card"
                                    variants={itemVariants}
                                    whileHover={{ 
                                        scale: 1.05,
                                        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.12)"
                                    }}
                                >
                                    <div className="soft-skill-header">
                                        <h4>{skill.name}</h4>
                                        <div className="skill-level-indicator">
                                            <div className="skill-level-bar" style={{ width: '85%' }}></div>
                                        </div>
                                    </div>
                                    <p>{skill.description}</p>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </Container>
        </section>
    );
};

export default Skills;